import { Link } from 'react-router-dom';
import { 
  Truck, Thermometer, Package, Zap, Clock, Users,
  ArrowRight, Phone, Shield
} from 'lucide-react';

const Fleet = () => {
  const equipment = [
    {
      id: 'dry-van',
      name: 'Dry Van Trailers',
      icon: Truck,
      specs: '53\' Enclosed',
      description: 'Standard enclosed trailers for general freight protection. E-track equipped for secure cargo management. Climate-resistant and theft-proof.',
      features: ['53-foot length', 'E-track systems', 'Air-ride suspension', 'Swing doors'],
      image: '/4.jpeg',
    },
    {
      id: 'reefer',
      name: 'Refrigerated Trailers',
      icon: Thermometer,
      specs: 'Temp Controlled',
      description: 'Temperature-controlled units capable of maintaining -20°F to 70°F. Continuous temperature monitoring and logging throughout transit.',
      features: ['-20°F to 70°F range', 'Digital monitoring', 'Multi-temp zones', 'Fuel-efficient units'],
      image: '/5.jpeg',
    },
    {
      id: 'flatbed',
      name: 'Flatbed Trailers',
      icon: Package,
      specs: 'Open Deck',
      description: 'Open deck trailers for oversized and heavy loads. Available in standard flatbed, step deck, and double-drop configurations.',
      features: ['48-53\' lengths', 'Up to 48,000 lbs', 'Tarping available', 'Chains & straps included'],
      image: '/6.jpeg',
    },
    {
      id: 'power-only',
      name: 'Power Units',
      icon: Zap,
      specs: 'Tractors Only',
      description: 'Late-model power units for pulling your trailers. Well-maintained tractors with experienced drivers for drop-and-hook operations.',
      features: ['Late-model trucks', 'GPS tracked', 'ELD equipped', 'Experienced drivers'],
      image: '/7.jpeg',
    },
    {
      id: 'box-truck',
      name: 'Box Trucks',
      icon: Truck,
      specs: '16-26\' Units',
      description: 'Smaller enclosed units perfect for LTL shipments and local deliveries. Liftgate service available for ground-level access.',
      features: ['16-26\' box sizes', 'Liftgate available', 'Residential delivery', 'Urban access'],
      image: '/8.jpeg',
    },
  ];

  return (
    <div className="bg-white" data-testid="fleet-page">
      {/* Hero Header */}
      <section className="bg-[#0B2A5B] py-20" data-testid="fleet-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#FFD700] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
            Our Fleet
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight font-['Montserrat'] mb-4">
            Fleet & Equipment
          </h1>
          <p className="text-white/70 max-w-2xl mx-auto">
            Our versatile fleet supports diverse freight needs nationwide. Well-maintained equipment ready for your cargo.
          </p>
        </div>
      </section>

      {/* Equipment Grid */}
      <section className="section-padding" data-testid="equipment-grid">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {equipment.map((item) => (
              <div
                key={item.id}
                className="equipment-card bg-white border border-slate-200 rounded-sm overflow-hidden group hover:shadow-xl transition-all"
                data-testid={`equipment-card-${item.id}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A5B]/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-[#C62828] p-2 rounded-sm">
                        <item.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold font-['Montserrat']">
                          {item.name}
                        </h3>
                        <span className="text-[#FFD700] text-xs font-semibold uppercase">
                          {item.specs}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-[#6B7280] text-sm mb-4 leading-relaxed">
                    {item.description}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {item.features.map((feature, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-[#C62828] rounded-full"></div>
                        <span className="text-xs text-[#6B7280]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expedited & Team Driving Banner */}
      <section className="bg-[#F8FAFC] py-16" data-testid="team-driving-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0B2A5B] rounded-sm p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-[#C62828] p-2 rounded-sm">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div className="bg-[#C62828] p-2 rounded-sm">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white font-['Montserrat'] mb-4">
                  Expedited & Team Driving Capability
                </h2>
                <p className="text-white/70 mb-6">
                  When time is critical, our expedited and team driving services keep your freight moving. With dedicated routes and two-driver teams, we minimize transit times for your most urgent shipments.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-sm">
                    <Shield className="h-4 w-4 text-[#FFD700]" />
                    <span className="text-white text-sm">24/7 Dispatch</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-sm">
                    <Clock className="h-4 w-4 text-[#FFD700]" />
                    <span className="text-white text-sm">Direct Routes</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-sm">
                    <Users className="h-4 w-4 text-[#FFD700]" />
                    <span className="text-white text-sm">Team Drivers</span>
                  </div>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-[#FFD700] text-sm font-semibold mb-2">Need Expedited Service?</p>
                <a
                  href="tel:773-344-7748"
                  className="inline-flex items-center space-x-2 bg-[#C62828] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#B71C1C] transition-all"
                  data-testid="expedited-call-btn"
                >
                  <Phone className="h-5 w-5" />
                  <span>Call Dispatch Now</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-banner py-16" data-testid="fleet-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Montserrat']">
            Have Specific Equipment Requirements?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Contact our dispatch team to discuss your freight needs and equipment requirements.
          </p>
          <Link
            to="/contact"
            className="bg-white text-[#C62828] px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-slate-100 transition-all shadow-lg inline-flex items-center justify-center space-x-2"
            data-testid="fleet-cta-quote"
          >
            <span>Request a Quote</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Fleet;
